import { useState } from 'react'
import { Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface LikeButtonProps {
  postId: string
  initialLiked?: boolean
  initialCount?: number
  size?: 'sm' | 'md' | 'lg'
  className?: string
  onLike?: (postId: string, liked: boolean) => void
}

export function LikeButton({ 
  postId, 
  initialLiked = false, 
  initialCount = 0, 
  size = 'md',
  className,
  onLike 
}: LikeButtonProps) {
  const [liked, setLiked] = useState(initialLiked)
  const [count, setCount] = useState(initialCount)
  const [isAnimating, setIsAnimating] = useState(false)

  const handleLike = () => {
    const newLiked = !liked
    setLiked(newLiked)
    setCount(prev => newLiked ? prev + 1 : prev - 1)
    setIsAnimating(true)
    
    // 触发动画效果
    setTimeout(() => setIsAnimating(false), 300)
    
    // 调用回调函数
    onLike?.(postId, newLiked)
  }

  const sizeClasses = {
    sm: 'h-7 px-2 text-xs',
    md: 'h-8 px-3 text-sm',
    lg: 'h-9 px-4 text-base'
  }

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  return (
    <Button
      variant={liked ? "default" : "ghost"}
      size="sm"
      onClick={handleLike}
      className={cn(
        sizeClasses[size],
        "gap-1.5 transition-all duration-200 hover:scale-105",
        liked 
          ? "bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-md shadow-red-500/25" 
          : "text-gray-600 hover:text-red-500 hover:bg-red-50",
        isAnimating && "animate-pulse",
        className
      )}
    >
      <Heart 
        className={cn(
          iconSizes[size],
          "transition-all duration-200",
          liked ? "fill-current" : "",
          isAnimating && "animate-bounce"
        )} 
      />
      {count > 0 && (
        <span className={cn(
          "font-medium transition-all duration-200",
          isAnimating && "animate-pulse"
        )}>
          {count > 999 ? `${(count / 1000).toFixed(1)}k` : count}
        </span>
      )}
    </Button>
  )
}

// 点赞动画效果组件
export function LikeAnimation({ show, onComplete }: { show: boolean, onComplete: () => void }) {
  if (!show) return null

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center"
      onAnimationEnd={onComplete}
    >
      <div className="animate-ping">
        <Heart className="w-16 h-16 text-red-500 fill-current" />
      </div>
    </div>
  )
}